import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

function renderContent(md) {
  return md.split("\n").filter(Boolean).map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i} className="font-serif text-3xl text-neutral-900 mt-10 mb-4">{line.slice(3)}</h2>;
    if (line.startsWith("- ")) return <li key={i} className="text-neutral-600 ml-6 list-disc">{line.slice(2)}</li>;
    const html = line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-neutral-900">$1</strong>');
    return <p key={i} className="text-neutral-600 text-lg leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [nf, setNf] = useState(false);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNf(true)); }, [slug]);

  if (nf) return <div className="min-h-screen flex flex-col items-center justify-center gap-4"><p className="text-neutral-500">Post not found.</p><Link to="/blog" className="btn-ghost">Back to Blog</Link></div>;
  if (!post) return <div className="min-h-screen flex items-center justify-center text-neutral-400">Loading…</div>;

  const schema = {
    "@context": "https://schema.org", "@type": "BlogPosting",
    headline: post.seo_title || post.title, description: post.meta_description || post.excerpt,
    image: post.cover_image, author: { "@type": "Organization", name: "ChiCha Lounge" },
    datePublished: post.date,
  };

  return (
    <>
      <Seo title={post.seo_title || post.title} description={post.meta_description || post.excerpt} path={`/blog/${post.slug}`} image={post.cover_image} schema={schema} />
      <article>
        <section className="relative pt-40 pb-16 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-white/60 to-white" />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8">
            <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-6 text-sm"><ArrowLeft size={16} /> The ChiCha Guide</Link>
            <div className="flex flex-wrap gap-2 mb-5">
              {(post.tags || []).map((t) => <span key={t} className="text-[10px] uppercase tracking-widest text-neutral-900/70 border border-neutral-900/20 rounded-full px-2.5 py-1">{t}</span>)}
            </div>
            <h1 className="font-serif text-4xl md:text-6xl text-neutral-900 leading-tight">{post.title}</h1>
          </div>
        </section>

        <section className="pb-28 bg-transparent">
          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <Reveal><div>{renderContent(post.content)}</div></Reveal>
            <div className="mt-16 card-lux p-8 text-center">
              <h3 className="font-serif text-3xl text-neutral-900">Experience it yourself</h3>
              <p className="text-neutral-500 mt-3">Reserve your table at ChiCha Lounge on U Street.</p>
              <Link to="/reservations" className="btn-gold mt-6 inline-flex">Reserve a Table</Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
